import type { Date as PDate } from "../../generated/core/v1/date";

/**
 * Helpers for `core.v1.Date` (year, month 1-12, day; 0 = unspecified). Plan dates are calendar
 * dates with no time zone, so everything here works on the three numbers, never on JS time.
 */
export type { PDate };

export const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
export const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function pdate(year: number, month = 0, day = 0): PDate {
  return { year, month, day };
}

export function isSet(d: PDate | undefined | null): d is PDate {
  return !!d && d.year > 0;
}

/** A sortable number: yyyymmdd (unset month/day count as 0). */
export function dateKey(d: PDate | undefined | null): number {
  if (!d) return 0;
  return d.year * 10000 + d.month * 100 + d.day;
}

export function compareDates(a: PDate | undefined, b: PDate | undefined): number {
  return dateKey(a) - dateKey(b);
}

export function sameMonth(a: PDate, b: PDate): boolean {
  return a.year === b.year && a.month === b.month;
}

/** Whole months from a to b (b later = positive), by calendar month. */
export function monthsBetween(a: PDate, b: PDate): number {
  return (b.year - a.year) * 12 + (b.month - a.month);
}

export function addMonths(d: PDate, n: number): PDate {
  const idx = d.year * 12 + (d.month - 1) + n;
  const year = Math.floor(idx / 12);
  const month = (idx % 12) + 1;
  const dim = daysInMonth(year, month);
  return { year, month, day: d.day ? Math.min(d.day, dim) : 0 };
}

export function addYears(d: PDate, n: number): PDate {
  return addMonths(d, n * 12);
}

export function daysInMonth(year: number, month: number): number {
  return new globalThis.Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Fractional year, for chart x positions. */
export function yearFraction(d: PDate): number {
  const m = d.month ? d.month - 1 : 0;
  const day = d.day ? d.day - 1 : 0;
  return d.year + m / 12 + day / 365;
}

/** Today's date in the browser's zone (prefer the user's `currentDate` from the backend). */
export function todayLocal(): PDate {
  const now = new globalThis.Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

export function fromJsDate(d: globalThis.Date): PDate {
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

export function toJsDate(d: PDate): globalThis.Date {
  return new globalThis.Date(d.year, Math.max(0, d.month - 1), Math.max(1, d.day));
}

/** "2041-03-01" style ISO date (path keys, inputs). */
export function toIso(d: PDate): string {
  const p = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${p(d.year, 4)}-${p(Math.max(1, d.month))}-${p(Math.max(1, d.day))}`;
}

export function fromIso(s: string): PDate | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return undefined;
  return { year: +m[1], month: +m[2], day: +m[3] };
}

/** "17 April 1986", "April 2041" or "2041", depending on what's set. */
export function formatDate(d: PDate | undefined | null): string {
  if (!isSet(d)) return "";
  if (d.month && d.day) return `${d.day} ${MONTHS_FULL[d.month - 1]} ${d.year}`;
  if (d.month) return `${MONTHS_FULL[d.month - 1]} ${d.year}`;
  return String(d.year);
}

/** "March 2041". */
export function formatMonth(d: PDate | undefined | null): string {
  if (!isSet(d)) return "";
  return d.month ? `${MONTHS_FULL[d.month - 1]} ${d.year}` : String(d.year);
}

/** "Mar 2041". */
export function formatMonthShort(d: PDate | undefined | null): string {
  if (!isSet(d)) return "";
  return d.month ? `${MONTHS_SHORT[d.month - 1]} ${d.year}` : String(d.year);
}

/** "Thursday 10 September". */
export function formatWeekday(d: PDate): string {
  const js = toJsDate(d);
  const wd = js.toLocaleDateString("en-CA", { weekday: "long" });
  return `${wd} ${d.day} ${MONTHS_FULL[d.month - 1]}`;
}

/** Age in whole years on `on`. */
export function ageOn(dob: PDate | undefined, on: PDate): number | undefined {
  if (!isSet(dob)) return undefined;
  let age = on.year - dob.year;
  if (on.month < dob.month || (on.month === dob.month && on.day < dob.day)) age -= 1;
  return age;
}

/** "3 years", "18 months", "1 year and 2 months". */
export function formatDuration(months: number): string {
  const m = Math.abs(Math.round(months));
  if (m < 24) return `${m} month${m === 1 ? "" : "s"}`;
  const y = Math.floor(m / 12);
  const r = m % 12;
  return r ? `${y} years and ${r} month${r === 1 ? "" : "s"}` : `${y} years`;
}
